import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { formatCost } from "@/utils/costEstimation";
import { DollarSign, TrendingUp, FileText, BarChart3 } from "lucide-react";
import { BudgetManager } from "@/components/BudgetManager";
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { toast } from "sonner";

interface ChatCost {
  id: string;
  file_name: string;
  created_at: string;
  cost_tracking: {
    total: number;
    breakdown: {
      upload: number;
      chatQueries: number;
      rerunCaptions: number;
    };
  };
}

const COLORS = ['hsl(142 70% 49%)', 'hsl(142 70% 35%)', 'hsl(142 70% 60%)'];

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [chats, setChats] = useState<ChatCost[]>([]);
  const [stats, setStats] = useState({
    totalCost: 0,
    totalChats: 0,
    averageCost: 0,
    totalUploadCost: 0,
    totalChatQueriesCost: 0,
    totalRerunCost: 0,
  });

  useEffect(() => {
    if (!user) {
      navigate("/auth");
      return;
    }
    fetchCostData();
  }, [user, navigate]);

  const fetchCostData = async () => {
    try {
      const { data, error } = await supabase
        .from("chat_analyses")
        .select("id, file_name, created_at, cost_tracking")
        .eq("user_id", user?.id)
        .is("deleted_at", null)
        .order("created_at", { ascending: false });

      if (error) throw error;

      const chatsWithCosts = data as ChatCost[];
      setChats(chatsWithCosts);

      // Calculate statistics
      const totalCost = chatsWithCosts.reduce((sum, chat) => sum + (chat.cost_tracking?.total || 0), 0);
      const totalUploadCost = chatsWithCosts.reduce((sum, chat) => sum + (chat.cost_tracking?.breakdown?.upload || 0), 0);
      const totalChatQueriesCost = chatsWithCosts.reduce((sum, chat) => sum + (chat.cost_tracking?.breakdown?.chatQueries || 0), 0);
      const totalRerunCost = chatsWithCosts.reduce((sum, chat) => sum + (chat.cost_tracking?.breakdown?.rerunCaptions || 0), 0);

      setStats({
        totalCost,
        totalChats: chatsWithCosts.length,
        averageCost: chatsWithCosts.length > 0 ? totalCost / chatsWithCosts.length : 0,
        totalUploadCost,
        totalChatQueriesCost,
        totalRerunCost,
      });
    } catch (error) {
      console.error("Error fetching cost data:", error);
      toast.error(t("toast.costDataFailed"));
    } finally {
      setLoading(false);
    }
  };

  // Prepare data for charts
  const costTrendData = chats
    .slice()
    .reverse()
    .map((chat, index) => ({
      name: `Chat ${index + 1}`,
      date: new Date(chat.created_at).toLocaleDateString(),
      cost: chat.cost_tracking?.total || 0,
      cumulative: chats
        .slice()
        .reverse()
        .slice(0, index + 1)
        .reduce((sum, c) => sum + (c.cost_tracking?.total || 0), 0),
    }));

  const breakdownData = [
    { name: "Initial Processing", value: stats.totalUploadCost },
    { name: "Chat Queries", value: stats.totalChatQueriesCost },
    { name: "Rerun Captions", value: stats.totalRerunCost },
  ].filter(item => item.value > 0);

  const topCostChats = chats
    .slice()
    .sort((a, b) => (b.cost_tracking?.total || 0) - (a.cost_tracking?.total || 0))
    .slice(0, 5);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading cost data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
          לוח עלויות
        </h1>
        <p className="text-muted-foreground">עקוב אחר עלויות השימוש ב-AI שלך בכל הצ'אטים</p>
      </div>

      {/* Budget Manager */}
      <div className="mb-8">
        <BudgetManager />
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">עלות כוללת</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCost(stats.totalCost)}</div>
            <p className="text-xs text-muted-foreground">בכל הצ'אטים</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">סה"כ צ'אטים</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalChats}</div>
            <p className="text-xs text-muted-foreground">צ'אטים מנותחים</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">עלות ממוצעת</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCost(stats.averageCost)}</div>
            <p className="text-xs text-muted-foreground">לצ'אט</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">היקר ביותר</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCost(topCostChats[0]?.cost_tracking?.total || 0)}
            </div>
            <p className="text-xs text-muted-foreground truncate">
              {topCostChats[0]?.file_name || "אין נתונים"}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Cost Trend Chart */}
        <Card>
          <CardHeader>
            <CardTitle>מגמת עלויות לאורך זמן</CardTitle>
            <CardDescription>הוצאות מצטברות בצ'אטים</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={costTrendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis 
                  dataKey="date" 
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                />
                <YAxis 
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  tickFormatter={(value) => `$${value.toFixed(2)}`}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "var(--radius)",
                  }}
                  formatter={(value: number) => formatCost(value)}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="cumulative" 
                  stroke="hsl(142 70% 49%)" 
                  strokeWidth={2}
                  name="Cumulative Cost"
                  dot={{ fill: "hsl(142 70% 49%)" }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Cost Breakdown Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle>פילוח עלויות</CardTitle>
            <CardDescription>התפלגות לפי סוג פעולה</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={breakdownData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="hsl(142 70% 49%)"
                  dataKey="value"
                >
                  {breakdownData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "var(--radius)",
                  }}
                  formatter={(value: number) => formatCost(value)}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Top Cost Chats */}
      <Card>
        <CardHeader>
          <CardTitle>5 הצ'אטים היקרים ביותר</CardTitle>
          <CardDescription>צ'אטים עם עלויות השימוש הגבוהות ביותר ב-AI</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {topCostChats.map((chat, index) => (
              <div
                key={chat.id}
                className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/5 transition-colors cursor-pointer"
                onClick={() => navigate(`/viewer?id=${chat.id}`)}
              >
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-semibold text-sm">
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{chat.file_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(chat.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <div className="font-bold text-lg">
                      {formatCost(chat.cost_tracking?.total || 0)}
                    </div>
                    <div className="text-xs text-muted-foreground space-x-2">
                      {chat.cost_tracking?.breakdown?.upload > 0 && (
                        <span>Upload: {formatCost(chat.cost_tracking.breakdown.upload)}</span>
                      )}
                      {chat.cost_tracking?.breakdown?.chatQueries > 0 && (
                        <span>Queries: {formatCost(chat.cost_tracking.breakdown.chatQueries)}</span>
                      )}
                      {chat.cost_tracking?.breakdown?.rerunCaptions > 0 && (
                        <span>Reruns: {formatCost(chat.cost_tracking.breakdown.rerunCaptions)}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {topCostChats.length === 0 && (
              <p className="text-center text-muted-foreground py-8">
                עדיין אין צ'אטים עם נתוני עלות
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
